import { useState } from 'react';
import { Plus, Check, X, Info, AlertCircle, CalendarDays, ArrowRight } from 'lucide-react';
import {
  useLeaveRequests,
  useApproveLeave,
  useRejectLeave,
  useCancelLeave,
  useCreateLeave,
  useMyBalance,
} from '@/lib/queries';
import { useAuthStore } from '@/store/auth.store';
import { formatDateOnly } from '@isoft-people/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { PageTransition, FadeIn } from '@/components/ui/motion';

const TYPE_LABELS: Record<string, string> = {
  VACATION: 'Vacaciones',
  MEDICAL: 'Permiso médico',
  PERSONAL: 'Permiso personal',
  BEREAVEMENT: 'Duelo',
  MATERNITY: 'Maternidad',
  PATERNITY: 'Paternidad',
  STUDY: 'Estudios',
  REMOTE: 'Trabajo remoto',
  OTHER: 'Otro',
};

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'neutral' | 'info';

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  CANCELLED: 'neutral',
  ESCALATED: 'info',
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobado',
  REJECTED: 'Rechazado',
  CANCELLED: 'Cancelado',
  ESCALATED: 'Escalado',
};

export function LeavePage() {
  const user = useAuthStore((s) => s.user);
  const isApprover =
    user?.role === 'ADMIN' || user?.role === 'HR' || user?.role === 'MANAGER';

  const [tab, setTab] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [showForm, setShowForm] = useState(false);

  const { data: all } = useLeaveRequests({});
  const { data: balance } = useMyBalance();

  const items =
    tab === 'pending'
      ? all?.items?.filter((l: any) => l.status === 'PENDING')
      : tab === 'approved'
      ? all?.items?.filter((l: any) => l.status === 'APPROVED')
      : tab === 'rejected'
      ? all?.items?.filter((l: any) => l.status === 'REJECTED')
      : all?.items;

  const percentUsed = balance
    ? Math.round(((balance.taken ?? 0) / (balance.generated || 1)) * 100)
    : 0;

  // Próxima ausencia REAL del usuario: su próxima solicitud aprobada a futuro.
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
  const proximaAusencia = (all?.items ?? [])
    .filter(
      (l: any) =>
        l.employee?.id === user?.employeeId &&
        l.status === 'APPROVED' &&
        (l.endDate ?? l.startDate)?.slice(0, 10) >= todayStr,
    )
    .sort((a: any, b: any) => String(a.startDate).localeCompare(String(b.startDate)))[0];

  return (
    <PageTransition className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-subtle">
            <CalendarDays size={22} className="text-primary" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-font-primary">Permisos y Vacaciones</h1>
            <p className="mt-0.5 text-sm text-font-secondary">
              Gestiona tus solicitudes y aprobaciones
            </p>
          </div>
        </div>
        <Button variant="gradient" size="lg" onClick={() => setShowForm(true)}>
          <Plus size={16} /> Nueva solicitud
        </Button>
      </div>

      {/* Balance + Próxima ausencia */}
      <FadeIn delay={0.05} className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card hover className="p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-semibold text-font-primary">
              Saldo de Vacaciones
            </h2>
            <span className="text-xs text-font-tertiary">Año 2026</span>
          </div>
          <div className="flex items-end gap-3">
            <span className="text-5xl font-bold text-primary">
              {balance?.available ?? 15}
            </span>
            <span className="mb-2 text-sm font-medium text-font-secondary">
              días disponibles
            </span>
          </div>
          <div className="mt-5">
            <div className="mb-2 flex justify-between text-xs text-font-secondary">
              <span>{percentUsed}% utilizados</span>
              <span>
                {balance?.taken ?? 0} / {balance?.generated ?? 15} días
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-border-light">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary to-primary-light"
                style={{ width: `${percentUsed}%` }}
              />
            </div>
          </div>
        </Card>

        <Card hover className="p-6">
          <h2 className="text-base font-semibold text-font-primary">Próxima Ausencia</h2>
          {proximaAusencia ? (
            <>
              <p className="mt-4 text-xl font-bold text-font-primary">
                {formatDateOnly(proximaAusencia.startDate, { day: 'numeric', month: 'long' })}
                {proximaAusencia.endDate &&
                proximaAusencia.endDate.slice(0, 10) !== proximaAusencia.startDate.slice(0, 10)
                  ? ` – ${formatDateOnly(proximaAusencia.endDate, {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}`
                  : `, ${new Date(proximaAusencia.startDate).getFullYear()}`}
              </p>
              <p className="mt-1 text-sm text-font-secondary">
                {TYPE_LABELS[proximaAusencia.type] ?? proximaAusencia.type} ·{' '}
                {proximaAusencia.workingDays} día(s)
              </p>
              <Badge variant="success" dot className="mt-3">
                Aprobado
              </Badge>
            </>
          ) : (
            <div className="mt-4 flex flex-col items-start gap-1">
              <p className="text-sm text-font-secondary">No tienes ausencias próximas.</p>
              <p className="text-xs text-font-tertiary">
                Cuando tengas una solicitud aprobada a futuro, aparecerá aquí.
              </p>
            </div>
          )}
        </Card>
      </FadeIn>

      {/* Tabs + Tabla */}
      <FadeIn delay={0.1}>
      <Card className="overflow-hidden">
        <div className="flex flex-wrap gap-1 border-b border-border px-6">
          {(['all', 'pending', 'approved', 'rejected'] as const).map((t) => {
            const labels = {
              all: 'Todas',
              pending: 'Pendientes',
              approved: 'Aprobadas',
              rejected: 'Rechazadas',
            };
            return (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`relative px-4 py-4 text-sm font-semibold transition ${
                  tab === t ? 'text-primary' : 'text-font-secondary hover:text-font-primary'
                }`}
              >
                {labels[t]}
                {tab === t && (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-t bg-primary" />
                )}
              </button>
            );
          })}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-font-secondary">
                <th className="px-6 py-3">Tipo</th>
                <th className="px-4 py-3">Fechas</th>
                <th className="px-4 py-3">Días</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Aprobador</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((lr: any) => (
                <LeaveRow
                  key={lr.id}
                  lr={lr}
                  canApprove={
                    isApprover &&
                    (lr.status === 'PENDING' || lr.status === 'ESCALATED')
                  }
                />
              ))}
              {(!items || items.length === 0) && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-font-secondary">
                    Sin solicitudes
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      </FadeIn>

      {showForm && <LeaveFormModal onClose={() => setShowForm(false)} />}
    </PageTransition>
  );
}

function LeaveRow({ lr, canApprove }: { lr: any; canApprove: boolean }) {
  const approve = useApproveLeave();
  const reject = useRejectLeave();
  const cancel = useCancelLeave();
  const user = useAuthStore((s) => s.user);

  const handleReject = async () => {
    const reason = prompt('Motivo del rechazo:');
    if (!reason) return;
    await reject.mutateAsync({ id: lr.id, reason });
  };

  const isMine = lr.employeeId === user?.employeeId;
  const canCancel = isMine && (lr.status === 'PENDING' || lr.status === 'APPROVED');
  // Fechas @db.Date sin corrimiento de zona horaria.
  const fmt = (d: string) => formatDateOnly(d);
  // El aprobador es un User; su nombre vive en la relación employee.
  const approverName = lr.approver
    ? lr.approver.employee
      ? `${lr.approver.employee.firstName} ${lr.approver.employee.lastName}`
      : (lr.approver.email ?? '–')
    : '–';

  return (
    <tr className="border-b border-border-light transition-colors hover:bg-primary-subtle/40">
      <td className="px-6 py-4 font-medium text-font-primary">
        {TYPE_LABELS[lr.type] ?? lr.type}
      </td>
      <td className="px-4 py-4 text-font-secondary">
        <span className="inline-flex items-center gap-1">
          {fmt(lr.startDate)}
          <ArrowRight size={12} className="text-font-tertiary" />
          {fmt(lr.endDate)}
        </span>
      </td>
      <td className="px-4 py-4 font-semibold text-font-primary">{lr.workingDays}</td>
      <td className="px-4 py-4">
        <Badge variant={STATUS_VARIANTS[lr.status] ?? 'neutral'} dot>
          {STATUS_LABELS[lr.status] ?? lr.status}
        </Badge>
      </td>
      <td className="px-4 py-4 text-font-secondary">{approverName}</td>
      <td className="px-4 py-4 text-right">
        {canApprove && (
          <div className="inline-flex gap-1">
            <button
              onClick={() => approve.mutate(lr.id)}
              className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-light text-accent hover:bg-accent hover:text-white"
              title="Aprobar"
            >
              <Check size={14} />
            </button>
            <button
              onClick={handleReject}
              className="flex h-8 w-8 items-center justify-center rounded-md bg-red-50 text-error hover:bg-error hover:text-white"
              title="Rechazar"
            >
              <X size={14} />
            </button>
          </div>
        )}
        {canCancel && !canApprove && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => cancel.mutate(lr.id)}
            className="text-xs hover:text-error"
          >
            Cancelar
          </Button>
        )}
      </td>
    </tr>
  );
}

function LeaveFormModal({ onClose }: { onClose: () => void }) {
  const create = useCreateLeave();
  const [form, setForm] = useState({
    type: 'VACATION',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: new Date().toISOString().slice(0, 10),
    reason: '',
  });
  const [error, setError] = useState<string | null>(null);

  // calc days
  const start = new Date(form.startDate);
  const end = new Date(form.endDate);
  const days = Math.max(
    0,
    Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1,
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync(form);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error?.message ?? 'Error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form
        onSubmit={submit}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-xl anim-scale-in"
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-bold text-font-primary">Nueva Solicitud</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-font-secondary hover:text-font-primary"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-col gap-5 p-6">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-font-primary">
              Tipo de solicitud
            </span>
            <select
              className="input h-10"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-font-primary">
                Fecha inicio
              </span>
              <input
                type="date"
                className="input h-10"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-font-primary">
                Fecha fin
              </span>
              <input
                type="date"
                className="input h-10"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              />
            </label>
          </div>

          <div className="flex items-start gap-2 rounded-md bg-primary-subtle p-3 text-xs text-primary">
            <Info size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              Se descontarán {days} día(s) laborales de tu saldo de vacaciones.
            </span>
          </div>

          <div className="flex items-start gap-2 rounded-md bg-yellow-50 p-3 text-xs text-warning">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              Nota: Si tu solicitud supera 15 días laborales, será escalada
              automáticamente.
            </span>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-font-primary">
              Motivo (opcional)
            </span>
            <textarea
              className="input"
              rows={3}
              placeholder="Vacaciones familiares planificadas"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </label>

          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-error">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-border bg-bg px-6 py-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="gradient" disabled={create.isPending}>
            Enviar solicitud
          </Button>
        </div>
      </form>
    </div>
  );
}
